/**
 * The chat's tabs remembered in this browser across reloads, one entry per chat.
 *
 * Storage can be unavailable or hold anything, so reads fall back to nothing
 * remembered and writes that fail are dropped: the tabs then open as for a
 * chat never seen here.
 */
import type { TabArrangement } from './canvasTabsReducer';

const STORAGE_KEY_PREFIX = 'innex-canvas-tabs:';

const storageKey = (sessionId: string) => `${STORAGE_KEY_PREFIX}${sessionId}`;

const parseKeys = (value: unknown): readonly string[] | null =>
  Array.isArray(value) && value.every((key) => typeof key === 'string') ? value : null;

function parseArrangement(raw: string): TabArrangement | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const order = parseKeys((parsed as Record<string, unknown>).order);
  const closed = parseKeys((parsed as Record<string, unknown>).closed);
  return order && closed ? { order, closed } : null;
}

/** Return the tabs remembered for a chat, or null if none are. */
export function loadRememberedArrangement(sessionId: string | undefined): TabArrangement | null {
  if (sessionId === undefined) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(sessionId));
    return raw === null ? null : parseArrangement(raw);
  } catch {
    return null;
  }
}

/** Remember a chat's tabs. */
export function saveRememberedArrangement(sessionId: string, arrangement: TabArrangement): void {
  try {
    window.localStorage.setItem(storageKey(sessionId), JSON.stringify(arrangement));
  } catch {
    // Unavailable or full storage only costs the chat its remembered tabs.
  }
}
